/**
 * iter1031 — BHI Dashboard Grid (preview)
 *
 * 12-col responsive grid on desktop, single-col fallback on mobile.
 * В preview версията tile-овете са placeholder-и, които показват как
 * би изглеждало разположението. Реалната интеграция ще wrap-ва
 * съществуващите компоненти (AIHealthSummary, BhiHero и т.н.).
 */
import React from "react";
import {
  Brain, Gauge, Utensils, Sparkles, Activity, Target, Heart,
  FileText, ShieldCheck, TrendingUp, FlaskConical, Camera, Map,
} from "lucide-react";

const TILES = [
  // ── Journey hero — full width introduction ─────────────────
  {
    id: "patient-journey", priority: "hero", col: "col-span-12",
    icon: Map, iconTint: "text-pink-500", ring: "from-pink-500/25 to-rose-500/15",
    title: "Твоят път в Breast Health Intelligence",
    subtitle: "5-стъпков персонализиран маршрут",
    body: (
      <div className="pt-1">
        <div className="grid grid-cols-5 gap-2">
          {[
            { k: "onboard",  label: "Onboarding",     status: "done",     hint: "Профил + анамнеза" },
            { k: "assess",   label: "Оценка",         status: "done",     hint: "42 rf + метрики" },
            { k: "insight",  label: "AI Insight",     status: "current",  hint: "BHI + pathways" },
            { k: "plan",     label: "Персонален план",status: "upcoming", hint: "Nudges + goals" },
            { k: "monitor",  label: "Мониторинг",     status: "upcoming", hint: "Twin + Vault" },
          ].map((step, i, arr) => {
            const isDone = step.status === "done";
            const isCurrent = step.status === "current";
            const dotBg = isDone
              ? "bg-emerald-500 text-white"
              : isCurrent
                ? "bg-pink-500 text-white ring-4 ring-pink-500/20"
                : "bg-slate-200 text-slate-500 dark:bg-slate-800 dark:text-slate-400";
            const lineBg = isDone
              ? "bg-emerald-500"
              : (i > 0 && arr[i-1].status === "current") ? "bg-gradient-to-r from-pink-500 to-slate-200 dark:to-slate-800"
                                                        : "bg-slate-200 dark:bg-slate-800";
            return (
              <div key={step.k} className="flex flex-col items-center text-center">
                <div className="w-full flex items-center mb-2">
                  {i > 0 && <div className={`h-0.5 flex-1 ${lineBg}`} />}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${dotBg}`}>
                    {isDone ? "✓" : i + 1}
                  </div>
                  {i < arr.length - 1 && (
                    <div className={`h-0.5 flex-1 ${isDone ? "bg-emerald-500" : "bg-slate-200 dark:bg-slate-800"}`} />
                  )}
                </div>
                <div className={`text-xs font-bold ${isCurrent ? "text-pink-500" : ""}`}>{step.label}</div>
                <div className="text-[10px] opacity-60 mt-0.5">{step.hint}</div>
              </div>
            );
          })}
        </div>
        <div className="mt-3 pt-3 border-t border-dashed border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <div className="text-xs">
            <span className="opacity-60">Прогрес:</span>{" "}
            <span className="font-bold text-pink-500">40%</span>
            <span className="opacity-60"> — 2 от 5 стъпки завършени</span>
          </div>
          <button className="text-xs font-semibold text-pink-500 hover:underline">
            Продължи → AI Insight
          </button>
        </div>
      </div>
    ),
  },

  // ── AI Summary + BHI Score ────────────────────────────────
  {
    id: "ai-summary", priority: "hero", col: "col-span-12 xl:col-span-8",
    icon: Brain, iconTint: "text-purple-500", ring: "from-purple-500/20 to-pink-500/10",
    title: "AI обобщение за твоята гръдна интелигентност",
    subtitle: "Персонализирано, обновявано на всеки 24 ч.",
    body: (
      <div className="space-y-2 text-sm">
        <p className="opacity-80">
          Текущият ти BHI индекс е стабилен, но има 2 модифицируеми фактора,
          които биха свалили метаболитния риск за естрогенно-сигнално активиране
          с ~12% в следващите 90 дни.
        </p>
        <div className="flex gap-2 flex-wrap pt-1">
          <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-500 text-[10px] font-semibold">✓ 4 stabil</span>
          <span className="px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-500 text-[10px] font-semibold">△ 2 action</span>
          <span className="px-2 py-0.5 rounded-full bg-red-500/15 text-red-500 text-[10px] font-semibold">! 0 risk</span>
        </div>
      </div>
    ),
  },
  {
    id: "bhi-score", priority: "hero", col: "col-span-12 xl:col-span-4",
    icon: Gauge, iconTint: "text-emerald-500", ring: "from-emerald-500/20 to-teal-500/10",
    title: "BHI Score",
    subtitle: "Композитен риск-индекс",
    body: (
      <div className="flex flex-col items-center py-2">
        <div className="relative w-28 h-28">
          <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
            <circle cx="50" cy="50" r="42" strokeWidth="10" className="fill-none stroke-slate-200 dark:stroke-slate-800" />
            <circle cx="50" cy="50" r="42" strokeWidth="10" strokeLinecap="round"
                    strokeDasharray="264" strokeDashoffset="82"
                    className="fill-none stroke-emerald-500" />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="text-2xl font-bold">68</div>
            <div className="text-[10px] opacity-60">/ 100</div>
          </div>
        </div>
        <div className="mt-2 text-xs font-semibold text-emerald-500">Умерен · Стабилен</div>
      </div>
    ),
  },

  // ── Primary row ──────────────────────────────────────────
  {
    id: "nutritional-fp", priority: "primary", col: "col-span-12 md:col-span-6 xl:col-span-6",
    icon: Utensils, iconTint: "text-orange-500", ring: "from-orange-500/20 to-red-500/10",
    title: "Нутриционен отпечатък",
    subtitle: "5-осова радар диаграма",
    body: (
      <div className="flex justify-center py-2">
        <svg viewBox="0 0 200 160" className="w-full max-w-[220px]">
          {/* Static radar mock */}
          <polygon points="100,20 175,60 155,140 45,140 25,60"
                   className="fill-orange-500/10 stroke-orange-500/40" strokeWidth="1" />
          <polygon points="100,45 145,72 130,120 70,120 55,72"
                   className="fill-orange-500/30 stroke-orange-500" strokeWidth="1.5" />
          {["NIS","DIL","UIS","COL3","PRAL"].map((l, i) => {
            const positions = [[100,10],[185,55],[160,150],[40,150],[15,55]];
            return (
              <text key={l} x={positions[i][0]} y={positions[i][1]}
                    className="fill-current text-[9px] font-semibold" textAnchor="middle">{l}</text>
            );
          })}
        </svg>
      </div>
    ),
  },
  {
    id: "kegg-activity", priority: "primary", col: "col-span-12 md:col-span-6 xl:col-span-6",
    icon: Activity, iconTint: "text-fuchsia-500", ring: "from-fuchsia-500/20 to-purple-500/10",
    title: "KEGG активност",
    subtitle: "Heat-map на биологични пътища",
    body: (
      <div className="grid grid-cols-6 gap-1 py-1">
        {[0.9,0.7,0.4,0.3,0.6,0.8, 0.5,0.2,0.9,0.3,0.7,0.4, 0.8,0.6,0.3,0.5,0.9,0.7,
          0.4,0.9,0.6,0.8,0.3,0.5, 0.7,0.4,0.8,0.6,0.9,0.2].map((v, i) => (
          <div key={i}
               className="aspect-square rounded"
               style={{ backgroundColor: `rgba(217, 70, 239, ${v})` }} />
        ))}
      </div>
    ),
  },

  // ── Compact row 1 ────────────────────────────────────────
  {
    id: "nudge-feed", priority: "compact", col: "col-span-12 md:col-span-6 xl:col-span-4",
    icon: TrendingUp, iconTint: "text-sky-500", ring: "from-sky-500/20 to-blue-500/10",
    title: "Nudge Feed",
    subtitle: "Последни 3 препоръки",
    body: (
      <ul className="space-y-1.5 text-xs">
        {[
          { txt: "Добави 15g ленено семе към закуската — фитоестроген баланс", tint: "text-emerald-500" },
          { txt: "Активност: 3× 30 мин ходене през седмицата", tint: "text-amber-500" },
          { txt: "Sleep prep: Магнезий 200mg преди сън (14 дни)", tint: "text-sky-500" },
        ].map((n, i) => (
          <li key={i} className="flex gap-1.5 items-start">
            <span className={`${n.tint} font-bold`}>•</span>
            <span className="opacity-85">{n.txt}</span>
          </li>
        ))}
      </ul>
    ),
  },
  {
    id: "digital-twin", priority: "compact", col: "col-span-12 md:col-span-6 xl:col-span-4",
    icon: Sparkles, iconTint: "text-indigo-500", ring: "from-indigo-500/20 to-violet-500/10",
    title: "Digital Twin",
    subtitle: "18 активни близнака",
    body: (
      <div className="grid grid-cols-3 gap-1.5">
        {["Estrogen","Insulin","Thyroid","Vitamin D","Iron","Cortisol"].map((tw, i) => (
          <div key={tw} className="aspect-square rounded-lg bg-gradient-to-br from-indigo-500/20 to-violet-500/10 flex items-center justify-center">
            <span className="text-[9px] font-bold text-center opacity-80">{tw}</span>
          </div>
        ))}
      </div>
    ),
  },
  {
    id: "prevention-path", priority: "compact", col: "col-span-12 md:col-span-6 xl:col-span-4",
    icon: Target, iconTint: "text-rose-500", ring: "from-rose-500/20 to-pink-500/10",
    title: "Пътят към превенция",
    subtitle: "3 активни цели",
    body: (
      <div className="space-y-2">
        {[
          { l: "Фитоестроген", pct: 72 },
          { l: "Активност", pct: 45 },
          { l: "Sleep quality", pct: 88 },
        ].map((g) => (
          <div key={g.l}>
            <div className="flex justify-between text-[10px] font-semibold mb-0.5">
              <span>{g.l}</span><span className="opacity-60">{g.pct}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-rose-500 to-pink-500"
                   style={{ width: `${g.pct}%` }} />
            </div>
          </div>
        ))}
      </div>
    ),
  },

  // ── Compact row 2 ────────────────────────────────────────
  {
    id: "risk-factors", priority: "compact", col: "col-span-12 md:col-span-6 xl:col-span-4",
    icon: Heart, iconTint: "text-red-500", ring: "from-red-500/20 to-orange-500/10",
    title: "42 Risk Factors",
    subtitle: "Модифицируеми + референтни",
    body: (
      <div className="flex items-center justify-between py-2">
        <div>
          <div className="text-2xl font-bold">28</div>
          <div className="text-[10px] opacity-60">Попълнени</div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-emerald-500">14</div>
          <div className="text-[10px] opacity-60">Актив пътища</div>
        </div>
      </div>
    ),
  },
  {
    id: "pathway-vault", priority: "compact", col: "col-span-12 md:col-span-6 xl:col-span-4",
    icon: FlaskConical, iconTint: "text-teal-500", ring: "from-teal-500/20 to-emerald-500/10",
    title: "Pathway Vault",
    subtitle: "16 активни pathways",
    body: (
      <div className="flex flex-wrap gap-1">
        {["PI3K-AKT","IGF","ESR1","BRCA","MAPK","AMPK","mTOR","NF-kB","p53"].map((p) => (
          <span key={p} className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-teal-500/15 text-teal-600 dark:text-teal-400">
            {p}
          </span>
        ))}
      </div>
    ),
  },
  {
    id: "screening", priority: "compact", col: "col-span-12 md:col-span-6 xl:col-span-4",
    icon: Camera, iconTint: "text-blue-500", ring: "from-blue-500/20 to-indigo-500/10",
    title: "Проверки и изображения",
    subtitle: "Последна мамограма: 4м назад",
    body: (
      <div className="py-2 text-xs">
        <div className="flex justify-between mb-1"><span className="opacity-70">Плътност</span><span className="font-semibold">C</span></div>
        <div className="flex justify-between mb-1"><span className="opacity-70">BIRADS</span><span className="font-semibold text-emerald-500">2 · Benign</span></div>
        <div className="flex justify-between"><span className="opacity-70">Следваща</span><span className="font-semibold">Май 2026</span></div>
      </div>
    ),
  },

  // ── Footer row ───────────────────────────────────────────
  {
    id: "compliance", priority: "compact", col: "col-span-12 md:col-span-6 xl:col-span-6",
    icon: ShieldCheck, iconTint: "text-emerald-600", ring: "from-emerald-500/20 to-green-500/10",
    title: "Compliance & прозрачност",
    subtitle: "GDPR · EU AI Act · Wellness class",
    body: (
      <div className="flex flex-wrap gap-1.5 pt-1">
        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">GDPR</span>
        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-500/15 text-blue-600 dark:text-blue-400">EU AI Act · Minimal risk</span>
        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-500/15 text-purple-600 dark:text-purple-400">Educational only</span>
        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-500/15 text-amber-600 dark:text-amber-400">Not a medical device</span>
      </div>
    ),
  },
  {
    id: "export", priority: "compact", col: "col-span-12 md:col-span-6 xl:col-span-6",
    icon: FileText, iconTint: "text-slate-500", ring: "from-slate-500/20 to-slate-400/10",
    title: "Експорт & PDF",
    subtitle: "Клинично досие за лекаря ти",
    body: (
      <div className="flex gap-2 pt-1">
        <button className="flex-1 text-xs py-1.5 rounded-lg bg-slate-900 text-white dark:bg-white dark:text-slate-900 font-semibold">
          PDF резюме
        </button>
        <button className="flex-1 text-xs py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 font-semibold">
          YAML export
        </button>
      </div>
    ),
  },
];

export default function BhiDashboardGrid({ isDark = false }) {
  const tileCard = isDark
    ? "bg-[hsl(222,47%,10%)] border-[hsl(223,47%,18%)]"
    : "bg-white border-slate-200";
  const subtle = isDark ? "text-slate-400" : "text-slate-500";

  return (
    <div className="flex-1 min-w-0">
      <div className="grid grid-cols-12 gap-4" data-testid="bhi-dashboard-grid">
        {TILES.map((t) => {
          const Icon = t.icon;
          return (
            <article
              key={t.id}
              id={`tile-${t.id}`}
              data-testid={`bhi-dash-tile-${t.id}`}
              className={`relative overflow-hidden rounded-2xl border ${tileCard} p-4 flex flex-col ${t.col}`}
            >
              {/* Corner glow */}
              <div className={`absolute -top-8 -right-8 w-28 h-28 rounded-full blur-2xl bg-gradient-to-br ${t.ring} pointer-events-none`} />

              {/* Header */}
              <div className="flex items-start gap-2.5 mb-3 relative">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-current/10 ${t.iconTint}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-bold leading-tight truncate">{t.title}</h3>
                  <p className={`text-[11px] ${subtle} mt-0.5 truncate`}>{t.subtitle}</p>
                </div>
              </div>

              {/* Body */}
              <div className="flex-1 relative">{t.body}</div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
